import { Badge, Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import { useState } from "react";
import UserService from "@/services/UserService";
import { showToast } from "@/components/ui/toaster";

const UserListItem = ({ user, onRefresh, onEdit }) => {
    const [isDeactivating, setIsDeactivating] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [isResettingTwoFactor, setIsResettingTwoFactor] = useState(false);

    const handleDeactivate = async () => {
        if (!window.confirm(`Deactivate ${user.firstName} ${user.lastName}?`)) return;
        setIsDeactivating(true);
        try {
            await UserService.deactivate(user.id);
            showToast("User deactivated", null, "success");
            onRefresh();
        } catch {
            showToast("Error", "Failed to deactivate user", "error");
        } finally {
            setIsDeactivating(false);
        }
    };

    const handleResend = async () => {
        setIsResending(true);
        try {
            await UserService.resendActivation(user.id);
            showToast("Activation email sent", null, "success");
        } catch {
            showToast("Error", "Failed to resend activation", "error");
        } finally {
            setIsResending(false);
        }
    };

    const handleResetTwoFactor = async () => {
        if (!window.confirm(`Reset two-factor authentication for ${user.firstName} ${user.lastName}? They will sign in with just their password until they set it up again.`)) return;
        setIsResettingTwoFactor(true);
        try {
            await UserService.resetTwoFactor(user.id);
            showToast("2FA reset", null, "success");
            onRefresh();
        } catch {
            showToast("Error", "Failed to reset 2FA", "error");
        } finally {
            setIsResettingTwoFactor(false);
        }
    };

    return (
        <Box
            w="full"
            bg={user.isActive ? "bg.secondary" : "bg.secondary"}
            borderRadius={12}
            px={5}
            py={4}
            borderWidth="1px"
            borderColor={user.isActive ? "border.primary" : "gray.700"}
        >
            <Flex justifyContent="space-between" alignItems="center" w="full" flexWrap="wrap" gap={3}>
                <HStack gap={4} flexWrap="wrap">
                    <Box>
                        <Text color={user.isActive ? "white" : "gray.500"} fontWeight="semibold">
                            {user.firstName} {user.lastName}
                        </Text>
                        <Text color={user.isActive ? "text.grey" : "gray.600"} fontSize={14}>{user.email}</Text>
                    </Box>
                    <Badge colorPalette={user.isActive ? (user.role === "Admin" ? "purple" : "blue") : "gray"} variant="subtle">
                        {user.role}
                    </Badge>
                    <Badge colorPalette={user.isActive ? "green" : "red"} variant="solid">
                        {user.isActive ? "Active" : "Inactive"}
                    </Badge>
                    {user.twoFactorEnabled && (
                        <Badge colorPalette="teal" variant="subtle">2FA</Badge>
                    )}
                </HStack>
                <HStack gap={2}>
                    {user.isActive && (
                        <Button size="sm" variant="outlined" textDecoration="unset" onClick={handleDeactivate} disabled={isDeactivating}>
                            Deactivate
                        </Button>
                    )}
                    {!user.isActive && (
                        <Button size="sm" variant="outlined" textDecoration="unset" onClick={handleResend} disabled={isResending}>
                            Resend activation
                        </Button>
                    )}
                    {user.twoFactorEnabled && (
                        <Button size="sm" variant="outlined" textDecoration="unset" onClick={handleResetTwoFactor} disabled={isResettingTwoFactor}>
                            Reset 2FA
                        </Button>
                    )}
                    <Button size="sm" variant="outlined" textDecoration="unset" onClick={() => onEdit(user)}>
                        Edit
                    </Button>
                </HStack>
            </Flex>
        </Box>
    );
};

export default UserListItem;
